import { useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate, useSearchParams } from 'react-router';
import * as DM from '@radix-ui/react-dropdown-menu';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ExternalLink, KeyRound, LogOut, Menu, PanelLeftClose, PanelLeftOpen, X } from 'lucide-react';
import { useAuth } from '@/auth/AuthContext';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Brand } from './Brand';
import { useBranding } from '@/lib/branding';
import { NAV, NAV_SECTIONS } from './nav';
import { OfficeWeatherPanel } from '@/components/booking/OfficeWeatherPanel';

interface PublicSettings {
  company: { name: string; shortName: string | null; logoFileId: string | null };
}

/** Nhãn của mục menu khớp đường dẫn hiện tại (khớp dài nhất) — dùng cho breadcrumb "Menu / …". */
function currentLabel(pathname: string): string {
  let best: { len: number; label: string } | null = null;
  for (const n of NAV) {
    if (n.external) continue;
    const ok = n.to === '/' ? pathname === '/' : pathname === n.to || pathname.startsWith(`${n.to}/`);
    if (ok && (!best || n.to.length > best.len)) best = { len: n.to.length, label: n.label };
  }
  if (best) return best.label;
  if (pathname.startsWith('/tai-khoan')) return 'Tài khoản & mật khẩu';
  return 'Trang chủ';
}

const COLLAPSE_KEY = 'sidebar-collapsed';
const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1';
  } catch {
    return false;
  }
};

/** Khung ứng dụng theo chuẩn giao diện chung (docs/UI_BRAND.md — lấy từ "Quản lý thi công"). */
export function AppLayout() {
  const { user, can, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  // Desktop: cho phép ẩn thanh menu bên trái (nhớ theo trình duyệt)
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const toggleCollapsed = () =>
    setCollapsed((v) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, v ? '0' : '1');
      } catch {
        /* bỏ qua khi không có localStorage */
      }
      return !v;
    });
  // ?embed=1: trang được nhúng (iframe) trong module khác → ẩn khung điều hướng
  const [params] = useSearchParams();
  const embedded = params.get('embed') === '1';
  const settings = useQuery({
    queryKey: ['settings', 'public'],
    queryFn: () => api.get<PublicSettings>('/settings/public'),
    staleTime: 300_000,
  });
  const sections = NAV_SECTIONS.map((s) => ({
    ...s,
    items: s.items.filter((n) => !n.perms || can(...n.perms)),
  })).filter((s) => s.items.length > 0);
  const itemClass = (active: boolean) =>
    cn(
      'flex items-center gap-[11px] rounded-[7px] px-2.5 py-[9px] text-[15px] font-semibold text-nav transition-colors hover:bg-slate-50 hover:text-ink',
      active && 'bg-brand-50 text-brand-700 hover:bg-brand-50 hover:text-brand-700',
    );
  const company = settings.data?.company;
  const brand = useBranding();
  const roles = user?.roles.map((r) => r.name).join(', ');
  const initial = (user?.fullName || user?.username || '?').trim().slice(0, 1).toLocaleUpperCase('vi');

  const sidebar = (
    <div className="flex h-full flex-col px-[17px] py-[25px]">
      <Link
        to="/"
        className="flex justify-center px-[9px] pb-7 pt-[3px]"
        onClick={() => setOpen(false)}
        aria-label={`${brand.title} — Trang chủ`}
      >
        <Brand size="xl" logoOnly />
      </Link>
      <nav className="min-h-0 flex-1 overflow-y-auto" aria-label="Điều hướng chính">
        {sections.map((sec, i) => (
          <div key={sec.title ?? i} className={cn(i > 0 && 'mt-5')}>
            <div className="px-2.5 pb-2 text-[12px] font-bold uppercase tracking-[1.25px] text-side-label">
              {sec.title ?? 'Menu'}
            </div>
            <div className="space-y-0.5">
              {sec.items.map((n) =>
                n.external ? (
                  // Module ứng dụng riêng: tải trang đầy đủ (không qua router của SPA này)
                  <a key={n.to} href={n.to} className={itemClass(false)} title={n.label}>
                    <n.icon className="size-[18px] shrink-0" />
                    <span className="min-w-0 leading-snug">{n.label}</span>
                    <ExternalLink className="ml-auto size-3 shrink-0 opacity-40" aria-hidden />
                  </a>
                ) : (
                  <NavLink
                    key={n.to}
                    to={n.to}
                    end={n.to === '/'}
                    title={n.label}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) => itemClass(isActive)}
                  >
                    <n.icon className="size-[18px] shrink-0" />
                    <span className="min-w-0 leading-snug">{n.label}</span>
                  </NavLink>
                ),
              )}
            </div>
          </div>
        ))}
      </nav>
      <div className="mt-4 grid gap-[9px] border-t border-line px-2 pt-3.5 text-sm text-[#a1a8b2]">
        <div className="flex items-center gap-[7px] font-semibold text-[#75685d]">
          <i className="size-[7px] rounded-full bg-[#44b887] shadow-[0_0_0_3px_#e9f6f0]" /> {brand.title}
        </div>
        <span className="truncate">{company?.name || 'Nền tảng quản trị doanh nghiệp'}</span>
      </div>
    </div>
  );

  if (embedded)
    return (
      <main className="px-3 py-4 sm:px-5">
        <Outlet />
      </main>
    );

  return (
    <div className={cn('min-h-screen', !collapsed && 'lg:pl-[236px]')}>
      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-30 hidden w-[236px] border-r border-line bg-white',
          !collapsed && 'lg:block',
        )}
      >
        {sidebar}
      </aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden" role="dialog" aria-modal="true" aria-label="Menu">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] border-r border-line bg-white shadow-xl">
            <button
              className="absolute right-2 top-5 rounded-md p-2 text-nav hover:bg-slate-100"
              onClick={() => setOpen(false)}
              aria-label="Đóng menu"
            >
              <X className="size-5" />
            </button>
            {sidebar}
          </aside>
        </div>
      )}
      <header className="sticky top-0 z-20 flex min-h-[55px] flex-wrap items-center gap-3 border-b border-line bg-white px-3 py-2 sm:px-6 lg:min-h-[63px] lg:px-[38px]">
        <button
          className="-ml-1 rounded-md p-2 text-nav hover:bg-slate-100 lg:hidden"
          onClick={() => setOpen(true)}
          aria-label="Mở menu"
        >
          <Menu className="size-5" />
        </button>
        <button
          className="-ml-2 hidden rounded-md p-2 text-nav hover:bg-slate-100 hover:text-ink lg:block"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Hiện thanh menu' : 'Ẩn thanh menu'}
          title={collapsed ? 'Hiện thanh menu' : 'Ẩn thanh menu'}
        >
          {collapsed ? <PanelLeftOpen className="size-5" /> : <PanelLeftClose className="size-5" />}
        </button>
        <Link to="/" className={cn(!collapsed && 'lg:hidden')} aria-label={`${brand.title} — Trang chủ`}>
          <Brand size="md" logoOnly />
        </Link>
        <div className="hidden min-w-0 flex-1 items-center gap-[11px] text-sm text-[#9aa2ac] lg:flex" data-testid="breadcrumbs">
          <span>Menu</span>
          <b className="font-normal text-[#c6cbd1]">/</b>
          <strong className="truncate font-semibold text-[#515b68]">{currentLabel(location.pathname)}</strong>
        </div>
        <div className="ml-auto flex w-full min-w-0 items-center justify-between gap-2 sm:w-auto sm:justify-end sm:gap-3">
          <OfficeWeatherPanel />
          {/*
            modal={false}: không khóa cuộn trang. Chế độ modal gắn overflow:hidden vào <body>; vì <html> có
            overflow-x:clip nên <body> thành vùng cuộn riêng và header sticky bị trôi mất khi đang cuộn giữa trang.
          */}
          <DM.Root modal={false}>
            <DM.Trigger
              className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1 text-sm hover:bg-slate-50"
              data-testid="user-menu"
              aria-label="Tài khoản"
            >
              <span className="grid size-[29px] place-items-center rounded-full bg-[#eaf5f0] text-sm font-bold text-[#347b61]">
                {initial}
              </span>
              <span className="hidden text-left sm:block">
                <span className="block max-w-44 truncate font-semibold leading-tight text-ink">{user?.fullName}</span>
                <span className="block max-w-44 truncate text-xs leading-tight text-[#87909d]">{roles}</span>
              </span>
              <ChevronDown className="size-4 text-[#a0a8b2]" />
            </DM.Trigger>
            <DM.Portal>
              <DM.Content
                align="end"
                sideOffset={6}
                collisionPadding={8}
                className="z-50 min-w-52 max-w-[calc(100vw-16px)] rounded-lg border border-line bg-white p-1 shadow-lg"
              >
                <div className="border-b border-line px-2 py-2 sm:hidden">
                  <div className="text-sm font-semibold text-ink">{user?.fullName}</div>
                  <div className="text-xs text-[#87909d]">{roles}</div>
                </div>
                <DM.Item
                  onSelect={() => navigate('/tai-khoan')}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm outline-none data-[highlighted]:bg-slate-100"
                >
                  <KeyRound className="size-4" /> Tài khoản & mật khẩu
                </DM.Item>
                <DM.Separator className="my-1 h-px bg-slate-100" />
                <DM.Item
                  onSelect={async () => {
                    await logout();
                    navigate('/dang-nhap');
                  }}
                  className="flex cursor-pointer items-center gap-2 rounded px-2 py-2 text-sm text-red-600 outline-none data-[highlighted]:bg-red-50"
                >
                  <LogOut className="size-4" /> Đăng xuất
                </DM.Item>
              </DM.Content>
            </DM.Portal>
          </DM.Root>
        </div>
      </header>
      <main className="mx-auto max-w-[1600px] px-3 py-5 sm:px-6 sm:py-6 lg:px-[38px] lg:py-8">
        <Outlet />
      </main>
    </div>
  );
}
